// editsScene.js
//
// The small standalone 3D scene behind the "Show model edits" drawer.
//
// It is deliberately NOT the streaming viewer. That pipeline exists to
// hold a whole building in memory at interactive framerates, and pays
// for it with a global instance pool whose materials are shared across
// every tile - which is exactly why the diff overlay has to ghost by
// grey TINT instead of transparency (see helpers/diffOverlay.js).
//
// Here the scene is a few dozen fragments, so each instance can own its
// own material and the ghost can be REAL transparency. That is what
// makes the old↔new slider read: at either end one generation is solid
// and the other is a see-through shell you look at it through.
//
// Fragment GLBs are content-addressed and immutable, so the OLD push's
// geometry is still downloadable long after its manifest was replaced -
// the server hands us the per-instance matrix out of that version's tile
// sidecars (back/utils/editFragments.js) and we place it here.
import {
  Scene, PerspectiveCamera, WebGLRenderer, Matrix4, Mesh, InstancedMesh, Box3, Sphere,
  Vector3, Color, MeshStandardMaterial, HemisphereLight, DirectionalLight, Raycaster,
  Vector2, Vector4, Quaternion, Spherical, MathUtils, MOUSE, Group, Clock,
} from "three";
import CameraControls from "camera-controls";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { bucketMeshesByLod, mergeLodParts } from "webgl/js/loader/streaming/lodPartMerge";
import { CHANGE_COLORS } from "helpers/diffOverlay";

// THE CAMERA IS THE MAIN VIEWER'S CAMERA.
//
// This scene is its own little renderer, but a coordinator who orbits a
// model in /model-view and then opens a revision must not have to learn a
// second set of mouse habits. So the controls, the button map and the
// clipping planes are lifted verbatim from webgl/js/viewer/index.js:
// left = orbit/roll, right = pan, MIDDLE = dolly (and the wheel too),
// dolly toward the cursor, infinite dolly past the target, and a fixed
// near/far of 0.05 / 600000 rather than a frustum recomputed per fit.
//
// That last one matters more than it looks: this scene used to set
// near = dist/1000 and far = dist*20 every time it framed something, so
// zooming into a door clipped the wall behind it away at a different
// distance than the main viewer would. Same numbers now, same clipping.
const subsetOfTHREE = {
  MOUSE, Vector2, Vector3, Vector4, Quaternion, Matrix4, Spherical, Box3, Sphere, Raycaster,
  MathUtils: { DEG2RAD: MathUtils.DEG2RAD, clamp: MathUtils.clamp },
};
CameraControls.install({ THREE: subsetOfTHREE });

// webgl/js/viewer/index.js - initControls(), verbatim.
function applyViewerControlScheme(controls) {
  controls.enabled = true;

  controls.mouseButtons.left = CameraControls.ACTION.ROTATE;
  controls.mouseButtons.right = CameraControls.ACTION.TRUCK;
  controls.mouseButtons.middle = CameraControls.ACTION.DOLLY;
  controls.mouseButtons.wheel = CameraControls.ACTION.DOLLY;

  controls.touches.one = CameraControls.ACTION.TOUCH_ROTATE;
  controls.touches.two = CameraControls.ACTION.TOUCH_DOLLY_TRUCK;
  controls.touches.three = CameraControls.ACTION.TOUCH_TRUCK;

  controls.minDistance = 2;
  controls.maxDistance = 1000;
  controls.dampingFactor = 0.05;
  controls.draggingDampingFactor = 0.25;
  controls.dollyToCursor = true;
  controls.infinityDolly = true;
  controls.dollySpeed = 0.6;
  controls.truckSpeed = 2.0;

  controls.maxPolarAngle = Math.PI;
  controls.minPolarAngle = 0;
}

// What a generation is painted in when the slider favours it.
//   new side - the diff's own vocabulary, so the drawer agrees with the
//              overlay and the change list.
//   old side - one colour for "this is how it was", because on the old
//              side "added" cannot occur and "modified"/"moved" would
//              otherwise be indistinguishable from their new selves.
//              A deletion is the exception: the old shape IS the change.
const PREV_COLOR = "#9B7FD4";
const GHOST_COLOR = "#9AA3A8";

// The unchanged model around the change. It is drawn by the SAME shader
// as a ghosted generation - one grey, transparent MeshStandardMaterial -
// so "ghost" means one thing in this scene: something you look through.
// (The streaming viewer's ghost is a grey TINT on an opaque instanced
// material, which is why Export history's "ghost unchanged" reads
// differently; here the ghost is real alpha.) Lower than a ghosted
// generation's 0.14: there is a lot of it, and stacked shells of context
// otherwise fog the change out.
//
// The context is the one thing in this scene that IS instanced. Every
// changed element owns its material because it has to be coloured, faded
// and lit on its own; the unchanged model never is - it is one grey, and
// it is thousands of elements. Drawing the whole export as separate
// meshes costs a draw call each; grouped by fragment it is a few hundred
// InstancedMeshes for the same picture.
const CONTEXT_OPACITY = 0.075;
const GHOST_OPACITY = 0.14;

export function sideColor(side, change) {
  if (side === "new") return CHANGE_COLORS[change] || CHANGE_COLORS.modified;
  return change === "removed" ? CHANGE_COLORS.removed : PREV_COLOR;
}

// A fragment GLB carries three LODs; this scene is small enough to
// always want the fine one.
const geometryCache = new Map(); // url -> Promise<{geometry}>

// Exported so the thumbnail renderer draws the SAME geometry the scene
// draws, out of the same cache - a fragment fetched for a preview is
// already in memory when the 3D view asks for it, and vice versa.
export function loadFragmentGeometry(url) {
  return loadFragment(url);
}

function loadFragment(url) {
  if (geometryCache.has(url)) return geometryCache.get(url);
  const p = fetch(url, { mode: "cors" })
    .then((r) => {
      if (!r.ok) throw new Error(`fragment ${r.status}`);
      return r.arrayBuffer();
    })
    .then((buf) => new Promise((resolve, reject) => {
      new GLTFLoader().parse(buf, "", (gltf) => {
        const buckets = bucketMeshesByLod(gltf.scene, 3);
        const fine = mergeLodParts((buckets[0] || []).map((m) => ({
          geometry: m.geometry, material: m.material, name: m.name,
        })));
        if (!fine || !fine.geometry) return reject(new Error("fragment has no geometry"));
        resolve({ geometry: fine.geometry });
      }, reject);
    }));
  geometryCache.set(url, p);
  return p;
}

/**
 * @param {HTMLElement} container
 * @param {{ onPick?: (uniqueId: string|null) => void }} opts
 */
export default function createEditsScene(container, { onPick } = {}) {
  const scene = new Scene();
  scene.background = new Color("#F4F6F7");
  // Same lens and the same clipping planes as the main viewer.
  const camera = new PerspectiveCamera(60, 1, 0.05, 600000);
  // POWER PREFERENCE MATCHES THE MAIN VIEWER, and it did not used to.
  //
  // This was "low-power", which reads as the obvious choice for a small
  // review scene. It is the wrong choice on ONE page and that page is
  // real: IssueResolutionPage renders IssueModelCanvas - a full viewer,
  // asking for high-performance since 2026-09-23 - and opens this drawer
  // OVER it. Two contexts on one page with opposite preferences is how a
  // switchable-graphics machine ends up running BOTH adapters at once,
  // which costs far more than this drawer saves.
  // DiffAnalyticsPage has no main viewer, so low-power was harmless
  // there; matching is harmless there too. The drawer is short-lived and
  // interactive - the thing to protect is not battery, it is the GPU the
  // viewer beside it is already using.
  const renderer = new WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  container.appendChild(renderer.domElement);
  renderer.domElement.style.width = "100%";
  renderer.domElement.style.height = "100%";
  renderer.domElement.style.display = "block";

  const controls = new CameraControls(camera, renderer.domElement);
  applyViewerControlScheme(controls);
  const clock = new Clock();

  scene.add(new HemisphereLight(0xffffff, 0x4a5560, 1.15));
  const key = new DirectionalLight(0xffffff, 0.75);
  key.position.set(1, 2, 1.4);
  scene.add(key);

  // One group per generation so "show old"/"show new" is a single flag
  // rather than a walk over every mesh.
  const groups = { old: new Group(), new: new Group(), context: new Group() };
  scene.add(groups.old, groups.new, groups.context);

  const meshes = [];        // { mesh, side, change, uniqueId, base: Color }
  // Unchanged neighbours, kept apart from `meshes` so they are never
  // coloured, never framed to, never picked, and survive a reload of the
  // change itself. One InstancedMesh per fragment, all sharing the single
  // ghost material below - they are never shaded individually.
  const ctxMeshes = [];     // { mesh: InstancedMesh, count }
  const ctxMaterial = new MeshStandardMaterial({
    color: new Color(GHOST_COLOR), roughness: 0.9, metalness: 0.02,
    transparent: true, opacity: CONTEXT_OPACITY, depthWrite: false,
  });
  let blend = 1;            // 0 = old, 1 = new
  // When a bucket tab is selected, everything outside it drops to the
  // ghost regardless of the slider - "show me what was deleted" means
  // the deletions in colour and the rest as context you look through.
  let changeFilter = null;  // null | 'added' | 'removed' | 'modified' | 'moved'
  let focusUid = null;
  let disposed = false;
  let raf = 0;
  // Fragments arrive in batches, and re-framing on each one wrenches the
  // camera out of the user's hands mid-load. Frame on the first batch
  // that produced something, and after that only when they have not
  // touched the camera themselves.
  let framedOnce = false;
  let userMovedCamera = false;
  // A row's "zoom to" pressed before that element's mesh has landed:
  // frame it the moment it does (see loadBatched's place callback).
  let pendingFocusUid = null;
  controls.addEventListener("controlstart", () => { userMovedCamera = true; });

  const ghost = new Color(GHOST_COLOR);

  function isLit(rec) {
    if (focusUid) return rec.uniqueId === focusUid;
    if (!changeFilter) return true;
    // 'modified' covers the moved/reshaped/replaced family; the panel
    // splits 'moved' out of it, so a moved element must not also light
    // up under 'modified'.
    return rec.change === changeFilter;
  }

  // One material, two ways to be a ghost. `w` is how strongly this mesh
  // is the thing being looked at: 1 = its own colour, solid; 0 = grey and
  // see-through.
  function shade(mesh, base, w, minOpacity) {
    mesh.material.color.copy(ghost).lerp(base, w);
    mesh.material.opacity = minOpacity + (1 - minOpacity) * w;
    // Writing depth for a nearly-transparent shell makes the solid
    // generation behind it disappear.
    mesh.material.depthWrite = w > 0.9;
    mesh.material.transparent = w <= 0.98;
    mesh.material.needsUpdate = true;
  }

  function paint() {
    for (const rec of meshes) {
      // The favoured side goes to its own colour at full opacity; the
      // other fades towards a transparent grey. Both move continuously,
      // so a half-way slider shows the change as a genuine cross-fade
      // rather than a hard toggle.
      const w = isLit(rec) ? (rec.side === "new" ? blend : 1 - blend) : 0;
      shade(rec.mesh, rec.base, w, GHOST_OPACITY);
    }
  }

  function resize() {
    const w = container.clientWidth || 1;
    const h = container.clientHeight || 1;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }

  const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null;
  if (ro) ro.observe(container);

  function tick() {
    if (disposed) return;
    raf = requestAnimationFrame(tick);
    controls.update(clock.getDelta());
    renderer.render(scene, camera);
  }
  resize();
  tick();

  function boundsOf(list) {
    const box = new Box3();
    let any = false;
    for (const rec of list) {
      if (!rec.mesh.parent) continue;      // detached, mid-reload
      box.expandByObject(rec.mesh);
      any = true;
    }
    return any && !box.isEmpty() ? box : null;
  }

  // Frame unless the user has taken over the camera; `force` is the
  // explicit "fit"/"zoom to element" button, which always wins.
  function autoFrame(force = false) {
    if (!force && framedOnce && userMovedCamera) return;
    const box = boundsOf(meshes);
    if (!box) return;
    frame(box, framedOnce);
    framedOnce = true;
  }

  // Framing keeps the direction the camera is already looking from and
  // only changes how far away it is, so a re-fit after the user has
  // orbited does not spin the model back to a canned angle.
  //
  // NOT camera-controls' own fitToBox, deliberately: that one snaps the
  // view to the nearest axis (roundToStep(theta, PI/2)) before it fits,
  // and a pure axis view hides exactly the relocation this drawer exists
  // to show. The near/far planes are untouched either way - they are the
  // main viewer's fixed 0.05/600000, so what clips here clips there.
  const _eye = new Vector3();
  const _tgt = new Vector3();
  const _right = new Vector3();
  const _up = new Vector3();
  const _corner = new Vector3();
  const _worldUp = new Vector3(0, 1, 0);

  // How far back the camera has to sit, along `dir`, for every corner of
  // `box` to land inside the frustum.
  //
  // Not the bounding SPHERE (radius / sin(fov/2)), which is what this
  // used to do: the things a revision changes are usually spread flat
  // across a floor plate, and a plate's diagonal is enormous next to its
  // on-screen height - fitting its sphere put the change in the middle
  // of an empty screen. This projects the eight corners onto the camera's
  // own axes and honours the pane's aspect, which is the difference
  // between a change filling the frame and a change being a speck in it.
  function distanceToFit(box, center, dir) {
    const tanV = Math.tan((camera.fov * Math.PI) / 360);
    const tanH = tanV * Math.max(camera.aspect || 1, 0.0001);
    _right.copy(_worldUp).cross(dir);
    if (_right.lengthSq() < 1e-8) _right.set(1, 0, 0);   // looking straight down
    _right.normalize();
    _up.copy(dir).cross(_right).normalize();
    let dist = 0;
    for (let i = 0; i < 8; i += 1) {
      _corner.set(
        i & 1 ? box.max.x : box.min.x,
        i & 2 ? box.max.y : box.min.y,
        i & 4 ? box.max.z : box.min.z
      ).sub(center);
      // depth along `dir` + how far back that corner's offset pushes us
      const z = _corner.dot(dir);
      dist = Math.max(
        dist,
        z + Math.abs(_corner.dot(_right)) / tanH,
        z + Math.abs(_corner.dot(_up)) / tanV
      );
    }
    return Math.max(dist, 1);
  }

  function frame(box, transition = false) {
    if (!box) return;
    const center = box.getCenter(new Vector3());
    if (framedOnce) {
      controls.getPosition(_eye);
      controls.getTarget(_tgt);
      _eye.sub(_tgt);
      if (_eye.lengthSq() < 1e-8) _eye.set(1, 0.85, 1);
    } else {
      // The opening angle: three-quarter, for the same reason.
      _eye.set(1, 0.85, 1);
    }
    _eye.normalize();
    const dist = distanceToFit(box, center, _eye) * 1.12;   // a little air
    _eye.multiplyScalar(dist);
    // setLookAt clamps the distance into [minDistance, maxDistance]; the
    // viewer's ceiling is 1000, and a change spread across a whole floor
    // needs more than that to sit inside the frustum. Lifting the ceiling
    // for the fit (and leaving it lifted, so the user can pull back to
    // where they were put) beats framing them inside the building.
    if (dist > controls.maxDistance) controls.maxDistance = dist * 1.5;
    controls.setLookAt(
      center.x + _eye.x, center.y + _eye.y, center.z + _eye.z,
      center.x, center.y, center.z, transition
    );
  }

  // Every instance owns its material - that is the whole reason this
  // scene exists next to the streaming viewer - so building one is the
  // same job for a changed element and for a ghosted neighbour.
  function buildMesh(geometry, color, matrix, uniqueId, side) {
    const material = new MeshStandardMaterial({
      color: color.clone(), roughness: 0.85, metalness: 0.02,
      transparent: true, opacity: 1, depthWrite: true,
    });
    const mesh = new Mesh(geometry, material);
    mesh.matrixAutoUpdate = false;
    if (Array.isArray(matrix) && matrix.length === 16) {
      // The sidecar stores the column-vector matrix written
      // row-major, which is precisely the order Matrix4.set takes.
      mesh.matrix = new Matrix4().set(...matrix);
    }
    mesh.userData.uniqueId = uniqueId;
    mesh.userData.side = side;
    groups[side].add(mesh);
    return mesh;
  }

  // Fetch a batch of fragments and hand each one to `place`. Shared by
  // the change and its context so both obey the same batch size: a
  // drawer opening must not fire hundreds of parallel fetches at the
  // same R2 the main viewer streams from.
  async function loadBatched(items, onProgress, place) {
    const total = items.length;
    let loaded = 0;
    let failed = 0;
    // Declared once, not per batch: a closure built inside the loop
    // closes over the counters it mutates, which is both a lint error
    // here and a real hazard the day someone stops awaiting the batch.
    const one = async (it) => {
      try {
        const { geometry } = await loadFragment(it.url);
        if (disposed) return;
        place(it, geometry);
      } catch (e) {
        failed += 1;
      } finally {
        loaded += 1;
        if (onProgress) onProgress({ loaded, total, failed });
      }
    };
    const BATCH = 12;
    for (let i = 0; i < items.length; i += BATCH) {
      if (disposed) return { loaded, total, failed };
      await Promise.all(items.slice(i, i + BATCH).map(one));
      paint();
      if (i === 0 || i + BATCH >= items.length) autoFrame();
    }
    return { loaded, total, failed };
  }

  const raycaster = new Raycaster();
  const ndc = new Vector2();
  // Left-drag orbits, and the browser fires `click` at the end of a drag
  // just the same - so a pick is only a pick if the pointer barely moved.
  let downAt = null;
  function onPointerDown(e) { downAt = [e.clientX, e.clientY]; }
  function onClick(e) {
    if (!onPick) return;
    const moved = downAt && Math.hypot(e.clientX - downAt[0], e.clientY - downAt[1]) > 4;
    downAt = null;
    if (moved) return;
    const rect = renderer.domElement.getBoundingClientRect();
    ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(ndc, camera);
    const hits = raycaster.intersectObjects([groups.old, groups.new], true);
    const hit = hits.find((h) => h.object && h.object.userData && h.object.userData.uniqueId);
    onPick(hit ? hit.object.userData.uniqueId : null);
  }
  renderer.domElement.addEventListener("pointerdown", onPointerDown);
  renderer.domElement.addEventListener("click", onClick);

  return {
    /**
     * @param {Array<{uniqueId, change, side, url, matrix}>} items
     * @param {(progress:{loaded:number,total:number,failed:number}) => void} onProgress
     */
    async setFragments(items, onProgress) {
      for (const rec of meshes) {
        // Detach before touching anything: the RAF loop is still running
        // and a mesh with a null geometry in the graph throws on render.
        // Geometry is NOT disposed - it is shared through the module
        // cache with every other instance of the same fragment.
        rec.mesh.parent && rec.mesh.parent.remove(rec.mesh);
        rec.mesh.material.dispose();
      }
      meshes.length = 0;
      framedOnce = false;
      userMovedCamera = false;

      const out = await loadBatched(items, onProgress, (it, geometry) => {
        const base = new Color(sideColor(it.side, it.colorChange || it.change));
        const mesh = buildMesh(geometry, base, it.matrix, it.uniqueId, it.side);
        const rec = { mesh, side: it.side, change: it.change, uniqueId: it.uniqueId, base };
        meshes.push(rec);
        // The element the reviewer already asked to zoom to has just
        // arrived: frame it now, and pin the camera so the batch/context
        // auto-frames that follow do not pull back to the whole change.
        if (pendingFocusUid && it.uniqueId === pendingFocusUid) {
          const box = boundsOf([rec]);
          if (box) { frame(box, true); framedOnce = true; userMovedCamera = true; pendingFocusUid = null; }
        }
      });
      paint();
      autoFrame();
      return out;
    },

    /**
     * The unchanged model around the change: same ghost shader, no
     * colour, no say in where the camera looks. Loaded separately from
     * the change (and after it) so the thing the drawer is about is on
     * screen first, and so toggling the context off and on again does
     * not reload the two generations.
     *
     * This is the whole rest of the export, not a handful of neighbours,
     * so it is drawn INSTANCED: one InstancedMesh per fragment, one
     * shared material for the lot. A thousand unchanged elements is a few
     * hundred draw calls instead of a thousand, and none of them can ever
     * need a colour of their own.
     *
     * Progress counts INSTANCES, not fragments - it sits next to a
     * checkbox that says how many elements are being ghosted.
     *
     * @param {Array<{uniqueId, url, matrix}>} items
     */
    async setContext(items, onProgress) {
      for (const rec of ctxMeshes) rec.mesh.parent && rec.mesh.parent.remove(rec.mesh);
      ctxMeshes.length = 0;
      if (!items || !items.length) return { loaded: 0, total: 0, failed: 0 };

      // Group by fragment first: the same wall type placed 300 times is
      // one geometry and one draw call.
      const byUrl = new Map();
      for (const it of items) {
        if (!it || !it.url || !Array.isArray(it.matrix) || it.matrix.length !== 16) continue;
        if (!byUrl.has(it.url)) byUrl.set(it.url, []);
        byUrl.get(it.url).push(it);
      }
      const groups_ = [...byUrl.entries()];
      const total = items.length;
      let loaded = 0;
      let failed = 0;
      const m4 = new Matrix4();
      const one = async ([url, rows]) => {
        try {
          const { geometry } = await loadFragment(url);
          if (disposed) return;
          const inst = new InstancedMesh(geometry, ctxMaterial, rows.length);
          for (let i = 0; i < rows.length; i += 1) {
            // Same convention as every other placement in this file:
            // the sidecar's column-vector matrix written row-major, which
            // is exactly the order Matrix4.set takes.
            inst.setMatrixAt(i, m4.set(...rows[i].matrix));
          }
          inst.instanceMatrix.needsUpdate = true;
          // Its instances are placed all over the model, so the mesh's
          // own bounds mean nothing to the culler.
          inst.frustumCulled = false;
          inst.userData.context = true;
          groups.context.add(inst);
          ctxMeshes.push({ mesh: inst, count: rows.length });
        } catch (e) {
          failed += rows.length;
        } finally {
          loaded += rows.length;
          if (onProgress) onProgress({ loaded, total, failed });
        }
      };
      const BATCH = 12;
      for (let i = 0; i < groups_.length; i += BATCH) {
        if (disposed) return { loaded, total, failed };
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(groups_.slice(i, i + BATCH).map(one));
      }
      return { loaded, total, failed };
    },
    setBlend(t) { blend = Math.max(0, Math.min(1, t)); paint(); },
    setSideVisible(side, visible) { if (groups[side]) groups[side].visible = !!visible; },
    // Light one bucket and ghost the rest. Pass null to light everything.
    setChangeFilter(change) {
      changeFilter = change || null;
      paint();
      const lit = meshes.filter(isLit);
      if (lit.length) frame(boundsOf(lit), true);
    },
    // Light a single element and ghost the rest, without moving the
    // camera - used when a row is picked in the list.
    setFocus(uniqueId) {
      focusUid = uniqueId || null;
      paint();
    },
    // Zoom to one element. An explicit zoom is a camera decision the
    // reviewer made, so it counts as "the user moved the camera": the
    // loader's automatic re-frames (after each batch, after the context
    // loads) must not pull the view back to the whole change - which is
    // what made the target button look dead while fragments were still
    // streaming in. A row picked before its mesh has landed is framed
    // when it does.
    focus(uniqueId) {
      const list = meshes.filter((m) => m.uniqueId === uniqueId);
      if (!list.length) {
        if (uniqueId) { pendingFocusUid = uniqueId; return true; }
        return false;
      }
      const box = boundsOf(list);
      if (!box) return false;
      pendingFocusUid = null;
      frame(box, true);
      framedOnce = true;
      userMovedCamera = true;
      return true;
    },
    fit() { pendingFocusUid = null; autoFrame(true); },

    // A picture of exactly what is on screen right now - the source of
    // every screenshot the issue actions file.
    //
    // The renderer is built without preserveDrawingBuffer (it costs
    // memory on every frame for something wanted a handful of times a
    // session), so the buffer is only readable in the same tick it was
    // drawn in. Render, then read, with nothing in between.
    capture(mime = "image/png") {
      if (disposed) return null;
      try {
        renderer.render(scene, camera);
        return renderer.domElement.toDataURL(mime);
      } catch (e) {
        return null;
      }
    },
    resize,
    dispose() {
      disposed = true;
      cancelAnimationFrame(raf);
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      renderer.domElement.removeEventListener("click", onClick);
      if (ro) ro.disconnect();
      for (const rec of meshes) rec.mesh.material.dispose();
      for (const rec of ctxMeshes) rec.mesh.dispose();   // instance buffers
      ctxMaterial.dispose();
      meshes.length = 0;
      ctxMeshes.length = 0;
      controls.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
    },
  };
}
